import { api } from "../../api/api";
import { IDirectOpportunity } from "./opportunites.types";
import { uploadMultipleFile } from "../../features/FileUpload/utils";
import { makeApiCall } from "../../helpers/postRequest";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { endPoints } from "../../constants/endPoints";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IFileUpload } from "../../features/FileUpload/components/FileUpload.type";
import { getUniqueId } from "../../helpers";

export const fetchOpportunitesList = createAsyncThunk<
  {
    data: IDirectOpportunity[];
    totalRecords: number;
  },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
    customer_id?: number;
  },
  { rejectValue: APIError }
>(
  "opportunites/fetchOpportunitesList",
  async (
    {
      pageNumber,
      rowsInPerPage,
      limit,
      status,
      date,
      searchValue,
      customer_id,
    },
    { rejectWithValue },
  ) => {
    try {
      let finalUrl = `${endPoints.opportunity.fetchOpportunities}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `${endPoints.opportunity.fetchOpportunities}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      if (customer_id) {
        finalUrl += "&customer_id=" + customer_id;
      }
      const res = await api.get(finalUrl);
      const data: IDirectOpportunity[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      return {
        data,
        totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOpportunites = createAsyncThunk<
  IDirectOpportunity,
  string,
  { rejectValue: APIError }
>(
  "opportunites/fetchOpportunites",
  async (leadId: string, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `${endPoints.opportunity.fetchOpportunities}?lead_id=${leadId}`,
      );
      const data = res.data.data;

      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of (data[0].file_upload || []) as any) {
          attachments.push({
            file: null,
            key: getUniqueId(),
            path: item,
          });
        }
        return { ...data[0], file_upload: attachments };
      } else {
        return rejectWithValue({
          message:
            "Oops! We couldn't find any records associated with your opportunites number at the moment. Please ensure that the provided information is accurate and try again.",
          status: 404,
        });
      }
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const upsertOpportunites = createAsyncThunk<
  IDirectOpportunity,
  {
    opportunites: IDirectOpportunity;
    initialData: IDirectOpportunity;
    isUpdate: boolean;
  },
  { rejectValue: APIError }
>(
  "opportunites/upsertOpportunites",
  async (
    payload: {
      opportunites: IDirectOpportunity;
      initialData: IDirectOpportunity;
      isUpdate: boolean;
    },
    { rejectWithValue },
  ) => {
    const { opportunites, initialData, isUpdate } = payload;
    try {
      const asPayload = {
        customer_id: opportunites.assigned_to_id,
        lead_id: opportunites.lead_id,
      };
      const { paths } = await uploadMultipleFile(
        opportunites.file_upload || [],
        "OPPORTUNITY",
        asPayload,
      );
      const res = await makeApiCall(
        {
          url: endPoints.opportunity.createOpportunity,
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "lead_id",
          },
        },
        initialData,
        {
          ...opportunites,
          file_upload: paths,
        },
      );
      return res.data.data as IDirectOpportunity;
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);
